import type { AdminConfigItemDto, AdminConfigValueType } from '@carcheck/shared';

/**
 * Client-side grouping + filtering for the runtime-config table (AUT-131).
 *
 * The backend `GET /admin/config` returns ALL ~44 keys whole, pre-ordered
 * by declaration order (`listConfigKeys()`), grouped by `<domain>.` prefix.
 * Search + type + group are CLIENT-side over this bounded set (architect
 * guardrail #7 — no server `q`/type params, no cursor). These helpers are
 * pure so the page spec can assert filtering without rendering.
 */

/** Filter state held by the page (URL-state is out of scope for this bounded set). */
export interface ConfigFilters {
  /** Free-text, matched case-insensitively against the human label + the key. */
  search: string;
  /** Selected value-type, or `null` for "all". */
  type: AdminConfigValueType | null;
  /** Selected domain group (the `<domain>` prefix), or `null` for "all". */
  group: string | null;
}

export const EMPTY_CONFIG_FILTERS: ConfigFilters = { search: '', type: null, group: null };

/**
 * The domain group of a key — the segment before the first dot
 * (`pricing.platformFeePercent` → `pricing`). A key with no dot falls back
 * to the whole key (defensive; the registry always uses `<domain>.<name>`).
 */
export const groupOf = (key: string): string => {
  const dot = key.indexOf('.');
  return dot === -1 ? key : key.slice(0, dot);
};

/**
 * Distinct domain groups in registry (= first-seen) order — feeds the
 * group filter dropdown. Order is preserved from the pre-ordered list so
 * the dropdown reads top-to-bottom like the table.
 */
export const distinctGroups = (items: AdminConfigItemDto[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const g = groupOf(item.key);
    if (!seen.has(g)) {
      seen.add(g);
      out.push(g);
    }
  }
  return out;
};

/**
 * Apply the client-side filters. Preserves input order (the list is
 * already grouped + ordered server-side). Type + group are exact-match
 * when set.
 *
 * Search is a case-insensitive substring over the human LABEL **and** the
 * key (AUT-284). The label match is the one that matters now that the row
 * no longer shows its key — an operator searching «комиссия» must find
 * `pricing.platformFeePercent`. The key match is kept because it costs
 * nothing and a developer told «посмотри pricing.platformFeePercent» can
 * still paste it in.
 *
 * `labelOf` is injected rather than imported so this stays pure and
 * testable without an i18n instance, and REQUIRED rather than optional so
 * a new call-site cannot silently reintroduce key-only search.
 */
export const filterConfigItems = (
  items: AdminConfigItemDto[],
  filters: ConfigFilters,
  labelOf: (key: string) => string,
): AdminConfigItemDto[] => {
  const needle = filters.search.trim().toLocaleLowerCase();
  return items.filter((item) => {
    if (needle.length > 0) {
      const haystack = `${item.key}\n${labelOf(item.key)}`.toLocaleLowerCase();
      if (!haystack.includes(needle)) return false;
    }
    if (filters.type !== null && item.type !== filters.type) return false;
    if (filters.group !== null && groupOf(item.key) !== filters.group) return false;
    return true;
  });
};

/** True when any filter is active (drives the Clear-filters link). */
export const hasActiveFilters = (filters: ConfigFilters): boolean =>
  filters.search.trim().length > 0 || filters.type !== null || filters.group !== null;
